import { styled } from '@linaria/react';
import { Group, Switch } from '@mantine/core';
import { Controls } from '@reactflow/controls';
import { memo, useMemo } from 'react';
import ReactFlow from 'reactflow';
import { Stages } from '../../../api/process-client/process-client.type';
import type { GraphData, ProcessMapping } from '../../../api/process-client/process-client.type';
import i18n from '../../../base-dictionary';
import { newCOLORS } from '../../../styles/colors';
import { baseBlack, secondaryBase } from '../../../styles/design-tokens';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import type { ProcessScope } from '../../process/context/process.context.type';
import { StageEdge } from '../../process/edges/stage-edge';
import { WorkflowEdgeIn, WorkflowEdgeOut } from '../../process/edges/workflow-edge';
import { StageNode } from '../../process/nodes/stage-node';
import { WorkflowNode } from '../../process/nodes/workflow-node';
import { ViewType } from '../../process/process.type';
import { calculateGraphV2 } from '../../process/processv2.helpers';
import { ViewToggle } from '../../process/scope/view-toggle';
import { WorkflowViewToggle } from '../../process/scope/workflow-view-toggle';
import { ProcessGraphLegend } from '../process-graph-legend';
import { stringifyProcessScope } from './process-graph.helpers';

const nodeTypes = {
  stageNode: StageNode,
  workflowNode: WorkflowNode,
};

const edgeTypes = {
  stageEdge: StageEdge,
  workflowEdgeIn: WorkflowEdgeIn,
  workflowEdgeOut: WorkflowEdgeOut,
};

type Props = {
  graphData: GraphData | undefined;
  mapping: ProcessMapping | null;
  stages: Stages[];
  viewType: ViewType;
  scope: ProcessScope;
  setScope: (scope: Partial<ProcessScope>) => void;
  isRightGraph?: boolean;
};
export const SectionGraph = memo(function SectionGraph({
  graphData,
  mapping,
  viewType,
  scope,
  setScope,
  stages,
  isRightGraph = false,
}: Props) {
  const { board, useWorkflow, useGroups } = scope;
  const workflowLabel = i18n.t('data_management.workflows.workflow');

  const { nodes, edges } = useMemo(
    () => calculateGraphV2(graphData, mapping, stages as Stages[], viewType, useGroups, isRightGraph),
    [graphData, mapping, stages, viewType, useGroups, isRightGraph],
  );

  const flowProps = useMemo(
    () => ({
      nodeOrigin: [0.5, 0.5] as [number, number],
      fitView: true,
      nodeTypes,
      edgeTypes,
      nodes,
      edges,
      proOptions: { hideAttribution: true },
      edgesFocusable: false,
      elementsSelectable: false,
    }),
    [nodes, edges],
  );

  return (
    <>
      <Group justify="space-between">
        <Text size="tiny" weight="medium">
          {stringifyProcessScope(scope)}
        </Text>
        <Group gap="xs">
          <Tooltip
            variant="black"
            label={
              !useWorkflow && !board
                ? `Select a ${workflowLabel} or ${i18n.t('common.board')} to view without groups.`
                : `Toggle to view with or without ${workflowLabel.toLowerCase()} groups`
            }
          >
            <Group gap="xs">
              <Text size="tiny">Use Groups</Text>

              <Switch
                checked={scope.useGroups}
                onChange={(event: any) => setScope({ useGroups: event.currentTarget.checked })}
                color={secondaryBase}
                disabled={!useWorkflow && !board}
              />
            </Group>
          </Tooltip>

          <Text size="tiny">Full View</Text>
          {mapping &&
            (useWorkflow ? (
              <WorkflowViewToggle mapping={mapping} useGroups={scope.useGroups} isRightGraph={isRightGraph} />
            ) : (
              <ViewToggle mapping={mapping} isRightGraph={isRightGraph} />
            ))}
        </Group>
      </Group>
      <StyledReactFlow {...flowProps}>
        <Controls position="bottom-right" showInteractive={false} />
        <ProcessGraphLegend
          legendItems={[
            {
              icon: 'square',
              color: baseBlack,
              text: i18n.t('process.section_graph.legend.expected_steps'),
              variant: 'filled',
            },
            {
              icon: 'square',
              color: newCOLORS.purple2,
              text: i18n.t('process.section_graph.legend.skipped_steps'),
              variant: 'filled',
            },
            {
              icon: 'square',
              color: newCOLORS.purple2,
              text: i18n.t('process.section_graph.legend.backwards_steps'),
              variant: 'outlined',
            },
            {
              icon: 'square',
              color: newCOLORS.gray3,
              text: i18n.t('process.section_graph.legend.blocked_steps'),
              variant: 'filled',
            },
          ]}
        />
      </StyledReactFlow>
    </>
  );
});

const StyledReactFlow = styled(ReactFlow)`
  .react-flow__handle {
    opacity: 0;
    border: none;
    min-height: 0;
  }

  .react-flow__controls {
    background: #f5f5f5;
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.3);
  }

  .react-flow__edge-path {
    stroke: #000;
  }
`;
